
/**
 * Meeting Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class MeetingService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                meta: response.meta || {},
                message: response.message || 'Meeting listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch meeting list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch meeting');
        }
    }

    async getReviewInputs(id) {
        try {
            const response = await withRetry(
                () => this.api.getReviewInputs(id),
                { maxRetries: 2 }
            );
            return {
                success: true,
                data: response.data,
                message: response.message || 'Review inputs loaded',
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch review inputs');
        }
    }

    async uploadYggReport(file, optionsOrCallback) {
        try {
            const response = await this.api.uploadYggReport(file, optionsOrCallback);
            return {
                success: true,
                data: response.data,
                message: response.message || 'YGG report uploaded',
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to upload YGG report');
        }
    }

    async discardUploadedYggReport(body) {
        try {
            const response = await this.api.discardUploadedYggReport(body);
            return {
                success: true,
                data: response.data,
                message: response.message || 'YGG report discarded',
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to discard YGG report');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const payload = data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Meeting oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create meeting');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const payload = data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Meeting güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update meeting');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Meeting silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete meeting');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        const results = { success: [], failed: [] };

        for (const id of ids) {
            try {
                await this.api.delete(id);
                results.success.push(id);
            } catch (error) {
                results.failed.push({ id, error: error.message });
            }
        }

        return {
            success: results.failed.length === 0,
            data: results,
            message: `${results.success.length}/${ids.length} öğe silindi`
        };
    }

    /**
     * Custom operation: createBatchDelete
     */
    async createBatchDelete(data = {}) {
        try {
            const response = await this.api.client.post(`/batch-delete`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createBatchDelete');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
